import { useEffect } from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
    LeaderboardCompetitionsApi,
    GetLeaderboardByIdOrSlugResponse,
    LeaderboardStandingEntry,
    LeaderboardStandingsResponse,
    LeaderboardMyRankEntry,
    LeaderboardMyRankResponse,
} from "@devour/client";
import getConfig from "@/utils/getConfig";

/**
 * A standing the table can render: the generated entry declares its core
 * fields optional, so the standings hook tightens them ONCE at the boundary
 * (derived from the generated type — never a hand-written mirror).
 */
export type RankedStanding = LeaderboardStandingEntry & {
    rank: number;
    displayName: string;
    value: number;
    eventCount: number;
};

export interface RankedStandingsData extends Omit<LeaderboardStandingsResponse, "standings"> {
    standings: RankedStanding[];
}

/**
 * Hook to fetch a single leaderboard
 *
 * @param idOrSlug - Leaderboard ID or URL slug
 */
export function useGetLeaderboard(idOrSlug: string | undefined) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: ["leaderboard", idOrSlug, fullToken?.id ?? "unauthenticated"],
        queryFn: (): Promise<GetLeaderboardByIdOrSlugResponse> =>
            new LeaderboardCompetitionsApi(getConfig(fullToken))
                .getLeaderboardCompetitionsIdOrSlug({ idOrSlug: idOrSlug! }),
        staleTime: 5 * 60 * 1000, // 5 minutes - WebSocket patches participant count
        refetchOnWindowFocus: false, // WebSocket handles updates
        enabled: !!idOrSlug,
    });
}

/**
 * Hook to fetch leaderboard standings
 *
 * Real-time updates are handled via WebSocket (useLeaderboardWebSocket hook).
 * This query is invalidated when the backend broadcasts standings changes.
 *
 * @param leaderboardId - Leaderboard ID
 * @param limit - Number of results per page
 * @param offset - Offset for pagination
 */
export function useGetLeaderboardStandings(
    leaderboardId: string | undefined,
    limit: number = 50,
    offset: number = 0,
    options?: { refetchInterval?: number },
) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: ["leaderboard-standings", leaderboardId, limit, offset, fullToken?.id ?? "unauthenticated"],
        queryFn: async (): Promise<RankedStandingsData> => {
            const response = await new LeaderboardCompetitionsApi(getConfig(fullToken))
                .getLeaderboardCompetitionsIdOrSlugStandings({
                    idOrSlug: leaderboardId!,
                    pageSize: limit,
                    page: Math.floor(offset / (limit || 50)) + 1,
                });
            return {
                ...response,
                standings: (response.standings ?? []).map(s => ({
                    ...s,
                    rank: s.rank ?? 0,
                    displayName: s.displayName ?? "Player",
                    value: s.value ?? 0,
                    eventCount: s.eventCount ?? 0,
                })),
            };
        },
        staleTime: 5 * 60 * 1000, // 5 minutes - WebSocket patches cache directly
        refetchOnWindowFocus: false, // WebSocket handles updates, no need to refetch on focus
        placeholderData: keepPreviousData,
        enabled: !!leaderboardId,
        /*
         * Opt-in polling for contexts that have no WebSocket provider (e.g. the
         * public /embed iframe). WS consumers omit this and keep patch-driven updates.
         */
        ...options?.refetchInterval ? { refetchInterval: options.refetchInterval } : {},
    });
}

/**
 * Hook to fetch user's own rank in a leaderboard
 *
 * @param leaderboardId - Leaderboard ID
 */
export function useGetMyLeaderboardRank(leaderboardId: string | undefined) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);

    return useQuery({
        queryKey: ["leaderboard-my-rank", leaderboardId, fullToken?.id],
        queryFn: async (): Promise<LeaderboardMyRankResponse | null> => {
            const response = await new LeaderboardCompetitionsApi(getConfig(fullToken))
                .getLeaderboardCompetitionsIdOrSlugMyRank({ idOrSlug: leaderboardId! });
            // a backend without this endpoint resolves void — React Query rejects undefined
            return response ?? null;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes - WebSocket triggers refetch on score change
        refetchOnWindowFocus: false, // WebSocket handles updates
        enabled: !!leaderboardId && !!fullToken?.token,
    });
}

/**
 * Batched my-rank lookup. List parents (carousel, browse page, hub, per-game
 * tab) collect every leaderboard id they're about to render, fire one POST
 * here, and provide the resulting Map via LeaderboardRanksContext so each
 * UnifiedCompetitionCard can read its rank without firing its own request.
 * Stable id key sort keeps React Query's cache hit-rate high across re-renders
 * that re-derive the array in a new order.
 */
export function useGetMyLeaderboardRanks(idOrSlugs: string[]) {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const queryClient = useQueryClient();
    const stableKey = [...idOrSlugs].sort().join(",");

    const query = useQuery({
        queryKey: ["leaderboard-my-ranks-batch", stableKey, fullToken?.id],
        queryFn: async (): Promise<Map<string, LeaderboardMyRankEntry>> => {
            const body = await new LeaderboardCompetitionsApi(getConfig(fullToken))
                .createLeaderboardCompetitionsMyRanks({
                    leaderboardCompetitionsMyRanksBody: { idOrSlugs },
                });
            const map = new Map<string, LeaderboardMyRankEntry>();
            for (const entry of body?.ranks ?? []) {
                map.set(entry.idOrSlug, entry);
            }
            return map;
        },
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: idOrSlugs.length > 0 && !!fullToken?.token,
    });

    /*
     * Seed the singleton-hook cache so any later call to useGetMyLeaderboardRank
     * (e.g. when the user navigates to /leaderboards/:id, or any other code path
     * outside a LeaderboardRanksProvider) reads the batched result as a cache
     * hit instead of firing its own GET /leaderboard-competitions/:id/my-rank.
     */
    useEffect(() => {
        if (!query.data) return;
        for (const [idOrSlug, entry] of query.data.entries()) {
            queryClient.setQueryData<LeaderboardMyRankResponse>(
                ["leaderboard-my-rank", idOrSlug, fullToken?.id],
                {
                    rank: entry.rank ?? null,
                    previousRank: entry.previousRank,
                    rankChange: entry.rankChange,
                    value: entry.value,
                    eventCount: entry.eventCount,
                    isParticipating: entry.isParticipating,
                },
            );
        }
    }, [query.data, queryClient, fullToken?.id]);

    return query;
}
